// ─── CONFIGURATION ─────────────────────────────────────────────────────────
// Public Storefront API access token (read_product_listings scope).
// This is the token sent in the X-Shopify-Storefront-Access-Token header below.
const STOREFRONT_ACCESS_TOKEN = "f622629f3f1ea9434f14ca3475ff9f28";

// Public Shopify API token (already exposed on the window object). Kept here for
// availability; not consumed by the Storefront query path — the header above uses
// STOREFRONT_ACCESS_TOKEN. Do not swap this into the Storefront header (auth would fail).
const SHOPIFY_API_TOKEN = "484ecf17463b34c66ecbc664fa73497f023a228a";

const STOREFRONT_API_VERSION = "2025-07";
const SWATCH_CDN_BASE =
  "https://cdn.shopify.com/s/files/1/0062/4354/9297/files/color-swatch--";
const SF_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

// Tag-based grouping: products that share a "YGroup_<group>" tag are one color family.
// Takes priority over the custom.sibling_group metaobject when present on a product.
const YGROUP_PREFIX = "ygroup_";

// ─── HELPERS ───────────────────────────────────────────────────────────────

// Extract the numeric Shopify ID from a GID string like "gid://shopify/Product/12345"
function numericId(gid) {
  if (!gid) return null;
  var parts = String(gid).split("/");
  return parts[parts.length - 1];
}

// Rebuy product.options entries can be plain strings OR objects ({name, position, values}).
// Normalize to the option name string.
function optName(o) {
  return String(o && o.name != null ? o.name : o);
}

// Returns the product's "YGroup_<group>" tag (case-insensitive) or null. Rebuy product
// tags can be an array or a comma-separated string.
function ygroupTagOf(product) {
  if (!product || !product.tags) return null;
  var tags = Array.isArray(product.tags)
    ? product.tags
    : String(product.tags).split(",");
  for (var i = 0; i < tags.length; i++) {
    var t = String(tags[i]).trim();
    if (t.toLowerCase().indexOf(YGROUP_PREFIX) === 0) return t;
  }
  return null;
}

// Normalizes Rebuy static-API metafields into a nested {namespace:{key:value}} lookup.
// The static API returns metafields as an array of {namespace, key, value} (or, on some
// payloads, already as a nested object). Mirrors the MKC metafieldsToMap helper.
function metafieldsToMap(metafields) {
  if (!metafields) return {};
  if (!Array.isArray(metafields)) {
    return typeof metafields === "object" ? metafields : {};
  }
  return metafields.reduce(function (acc, m) {
    if (!m || m.namespace == null || m.key == null) return acc;
    if (!acc[m.namespace]) acc[m.namespace] = {};
    acc[m.namespace][m.key] = m.value;
    return acc;
  }, {});
}

// Convert a color name to a URL slug: "Navy Blue" → "navy-blue"
function slugify(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Determine the display color name for a Rebuy static-API product.
 * Priority: theme.cutline metafield → first value of "Color" option → product title.
 * (Options can be {name,values} objects or strings; metafields come via metafieldsToMap.)
 */
function colorNameForRebuy(rp) {
  var mf = metafieldsToMap(rp && rp.metafields);
  if (mf.theme && mf.theme.cutline) return mf.theme.cutline;

  var colorOption = (rp?.options || []).find(function (o) {
    var n = optName(o).toLowerCase();
    return n.indexOf("color") !== -1 || n.indexOf("colour") !== -1;
  });
  if (colorOption && colorOption.values && colorOption.values[0]) {
    return colorOption.values[0];
  }

  return rp?.title || "";
}

// ─── STOREFRONT CLIENT ─────────────────────────────────────────────────────

var ShopifyStorefrontClient = (function () {
  var cache = {}; // in-memory first

  // custom.sibling_group is a metaobject_reference (type "product_siblings") whose
  // product_list field holds the sibling product GIDs. The metaobject's product_list
  // VALUE carries the full list; the Storefront references() connection only resolves
  // the subset published to this token's channel — so we parse the value and fetch the
  // full set from the Rebuy API instead. (A few merchants still use a direct
  // list.product_reference on the metafield itself; metafield.value covers that too.)
  var SIBLING_QUERY = `
    query SiblingGroup($id: ID!) {
      product(id: $id) {
        metafield(namespace: "custom", key: "sibling_group") {
          type
          value
          reference {
            ... on Metaobject {
              field(key: "product_list") { value }
            }
          }
        }
      }
    }
  `;

  function storefrontFetch(query, variables) {
    var shop =
      (window.Shopify && window.Shopify.shop) ||
      document
        .querySelector('link[rel="canonical"]')
        ?.href?.match(/https?:\/\/([^\/]+)/)?.[1] ||
      "";
    var endpoint =
      "https://" + shop + "/api/" + STOREFRONT_API_VERSION + "/graphql.json";

    return fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({ query: query, variables: variables }),
    }).then(function (r) {
      return r.json();
    });
  }

  function lsCacheKey(numId) {
    // v2: cached payload changed from Storefront product nodes → an array of sibling
    // product IDs (resolved through the product_siblings metaobject).
    return "rebuy_sg2_" + numId;
  }

  function lsRead(numId) {
    try {
      var raw = localStorage.getItem(lsCacheKey(numId));
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (Date.now() - entry.ts > SF_CACHE_TTL_MS) {
        localStorage.removeItem(lsCacheKey(numId));
        return null;
      }
      return entry.data;
    } catch (_) {
      return null;
    }
  }

  function lsWrite(numId, data) {
    try {
      localStorage.setItem(
        lsCacheKey(numId),
        JSON.stringify({ ts: Date.now(), data: data }),
      );
    } catch (_) {
      // Swallow QuotaExceededError silently
    }
  }

  var MEDIA_QUERY = `
    query MediaImagesByIds($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on MediaImage {
          id
          image { url }
        }
      }
    }
  `;

  function mediaCacheKey(gids) {
    return "rebuy_media_" + gids.join("_").replace(/[^a-zA-Z0-9]/g, "_");
  }

  return {
    resolveMediaImages: function (gids) {
      var unique = [];
      (gids || []).forEach(function (g) {
        if (g && unique.indexOf(g) === -1) unique.push(g);
      });
      if (unique.length === 0) return Promise.resolve({});

      var ckey = mediaCacheKey(unique);
      if (cache[ckey]) return Promise.resolve(cache[ckey]);

      try {
        var raw = localStorage.getItem(ckey);
        if (raw) {
          var entry = JSON.parse(raw);
          if (Date.now() - entry.ts <= SF_CACHE_TTL_MS) {
            cache[ckey] = entry.data;
            return Promise.resolve(entry.data);
          }
          localStorage.removeItem(ckey);
        }
      } catch (_) {}

      return storefrontFetch(MEDIA_QUERY, { ids: unique })
        .then(function (json) {
          var map = {};
          (json?.data?.nodes || []).forEach(function (n) {
            if (n && n.id && n.image && n.image.url) map[n.id] = n.image.url;
          });
          cache[ckey] = map;
          try {
            localStorage.setItem(
              ckey,
              JSON.stringify({ ts: Date.now(), data: map }),
            );
          } catch (_) {}
          return map;
        })
        .catch(function (err) {
          console.warn("[Rebuy Grouping] Storefront media resolve error", err);
          return {};
        });
    },

    /**
     * Resolve a color family by its shared "YGroup_<group>" tag.
     * Returns an array of member product numeric IDs (may be empty). Full product data
     * is then fetched from the Rebuy API, same as the metaobject path.
     */
    getGroupByTag: function (tag) {
      if (!tag) return Promise.resolve([]);

      var ckey = "rebuy_yg_" + tag.replace(/[^a-zA-Z0-9]/g, "_");
      if (cache[ckey]) return Promise.resolve(cache[ckey]);

      try {
        var raw = localStorage.getItem(ckey);
        if (raw) {
          var entry = JSON.parse(raw);
          if (Date.now() - entry.ts <= SF_CACHE_TTL_MS) {
            cache[ckey] = entry.data;
            return Promise.resolve(entry.data);
          }
          localStorage.removeItem(ckey);
        }
      } catch (_) {}

      var query =
        "query GroupByTag($q: String!) { products(first: 50, query: $q) { nodes { id } } }";
      var q = "tag:'" + String(tag).replace(/'/g, "\\'") + "'";

      return storefrontFetch(query, { q: q })
        .then(function (json) {
          var nodes = json?.data?.products?.nodes || [];
          var ids = nodes
            .map(function (n) {
              return numericId(n.id);
            })
            .filter(Boolean);
          cache[ckey] = ids;
          try {
            localStorage.setItem(
              ckey,
              JSON.stringify({ ts: Date.now(), data: ids }),
            );
          } catch (_) {}
          return ids;
        })
        .catch(function (err) {
          console.warn("[Rebuy Grouping] Storefront tag-group error for " + tag, err);
          return [];
        });
    },

    /**
     * Resolve the sibling group for a numeric Shopify product ID.
     * Returns an ordered array of sibling product numeric IDs (may be empty). The IDs
     * are read from the product_siblings metaobject's product_list VALUE so the full
     * family is returned regardless of Storefront channel publication; the full product
     * data is then fetched from the Rebuy API.
     */
    getSiblingGroup: function (numericProductId) {
      if (!numericProductId) return Promise.resolve([]);

      // In-memory cache hit
      if (cache[numericProductId])
        return Promise.resolve(cache[numericProductId]);

      // localStorage cache hit
      var ls = lsRead(numericProductId);
      if (ls) {
        cache[numericProductId] = ls;
        return Promise.resolve(ls);
      }

      var gid = "gid://shopify/Product/" + numericProductId;

      return storefrontFetch(SIBLING_QUERY, { id: gid })
        .then(function (json) {
          var mf = json?.data?.product?.metafield;
          // Metaobject reference → product_list field value; else a direct
          // list.product_reference value on the metafield itself.
          var listValue =
            (mf && mf.reference && mf.reference.field && mf.reference.field.value) ||
            (mf && mf.value) ||
            null;

          var ids = [];
          if (listValue) {
            try {
              var arr = JSON.parse(listValue);
              if (Array.isArray(arr)) {
                ids = arr.map(numericId).filter(Boolean);
              }
            } catch (_) {
              /* value wasn't a JSON array (e.g. a bare metaobject GID) → no siblings */
            }
          }

          cache[numericProductId] = ids;
          lsWrite(numericProductId, ids);
          return ids;
        })
        .catch(function (err) {
          console.warn(
            "[Rebuy Grouping] Storefront API error for product " +
              numericProductId,
            err,
          );
          return [];
        });
    },
  };
})();

// ─── REBUY CLIENT ──────────────────────────────────────────────────────────

var RebuyClient = (function () {
  // Per-PRODUCT cache (Oofos pattern): each full product JSON is cached in localStorage
  // keyed by id with a per-product cachedAt TTL — NOT keyed by the requested id-set. So a
  // product fetched once is reused across page loads and across any group it appears in;
  // only genuinely-missing ids hit the API. (Previously only an in-memory id-set→array
  // map existed, so every page load re-fetched the full product data for the whole group.)
  var STORAGE_KEY = "rebuy_pj"; // rebuy_pj:<productId>
  var mem = {}; // in-memory mirror of parsed products

  function readCached(id) {
    var cacheMax = Date.now() - SF_CACHE_TTL_MS;
    if (mem[id] && mem[id].cachedAt > cacheMax) return mem[id];
    try {
      var raw = localStorage.getItem(STORAGE_KEY + ":" + id);
      if (!raw) return null;
      var p = JSON.parse(raw);
      if (p && p.cachedAt > cacheMax) {
        mem[id] = p;
        return p;
      }
      localStorage.removeItem(STORAGE_KEY + ":" + id);
    } catch (_) {}
    return null;
  }

  // Evict the oldest half of rebuy-* cache entries when storage is full.
  function evictOldest() {
    var prefixes = ["rebuy_pj:", "rebuy_sg2_", "rebuy_yg_", "rebuy_media_"];
    var entries = [];
    for (var i = 0; i < localStorage.length; i++) {
      var k = localStorage.key(i);
      if (!k) continue;
      var matches = false;
      for (var p = 0; p < prefixes.length; p++) {
        if (k.indexOf(prefixes[p]) === 0) {
          matches = true;
          break;
        }
      }
      if (!matches) continue;
      var ts = 0;
      try {
        var parsed = JSON.parse(localStorage.getItem(k));
        ts = (parsed && (parsed.cachedAt || parsed.ts)) || 0;
      } catch (_) {}
      entries.push({ key: k, ts: ts });
    }
    entries.sort(function (a, b) {
      return a.ts - b.ts;
    });
    var removeCount = Math.max(1, Math.ceil(entries.length / 2));
    for (var j = 0; j < removeCount; j++) localStorage.removeItem(entries[j].key);
  }

  function writeCached(id, product) {
    product.cachedAt = Date.now();
    mem[id] = product;
    try {
      localStorage.setItem(STORAGE_KEY + ":" + id, JSON.stringify(product));
    } catch (e) {
      if (e && e.name === "QuotaExceededError") {
        evictOldest();
        try {
          localStorage.setItem(STORAGE_KEY + ":" + id, JSON.stringify(product));
        } catch (_) {}
      }
    }
  }

  function orderByRequested(list, ids) {
    var byId = {};
    list.forEach(function (p) {
      if (p && p.id != null) byId[String(p.id)] = p;
    });
    return ids
      .map(function (id) {
        return byId[String(id)];
      })
      .filter(Boolean);
  }

  return {
    /**
     * Fetch Rebuy-shaped products by numeric product IDs. Serves cached products from
     * localStorage and only requests the ids that aren't cached (or have expired).
     */
    getProductsByIDs: function (ids) {
      if (!ids || ids.length === 0) return Promise.resolve([]);

      var cached = [];
      var missing = [];
      ids.forEach(function (id) {
        var c = readCached(id);
        if (c) cached.push(c);
        else missing.push(id);
      });

      if (missing.length === 0) return Promise.resolve(orderByRequested(cached, ids));

      var apiKey =
        window.Rebuy && window.Rebuy.shop && window.Rebuy.shop.api_key;
      if (!apiKey) return Promise.resolve(orderByRequested(cached, ids));

      // metafields=yes so each sibling carries custom.swatch_override (the MediaImage
      // GID) — that metafield is NOT exposed to the Storefront API, so the Rebuy static
      // API is the only place to read it (MKC pattern).
      var url =
        "https://rebuyengine.com/api/v1/products/static?limit=50&metafields=yes&key=" +
        apiKey +
        "&ids=" +
        missing.join(",");

      return fetch(url)
        .then(function (r) {
          return r.json();
        })
        .then(function (json) {
          var fetched = (json && json.data) || [];
          fetched.forEach(function (p) {
            if (p && p.id != null) writeCached(p.id, p);
          });
          return orderByRequested(cached.concat(fetched), ids);
        })
        .catch(function (err) {
          console.warn("[Rebuy Grouping] Rebuy API error", err);
          return orderByRequested(cached, ids);
        });
    },
  };
})();

// ─── STITCH ────────────────────────────────────────────────────────────────

async function stitchGroupToProduct(product) {
  if (!product || !product.id) return;

  // Reset grouping on each stitch to avoid stale data
  product.productGrouping = [];

  // Resolve the family members. A shared "YGroup_<group>" tag takes priority (tag-based
  // grouping); otherwise fall back to the custom.sibling_group product_siblings metaobject.
  var ygTag = ygroupTagOf(product);
  var siblingIds;
  if (ygTag) {
    siblingIds = await ShopifyStorefrontClient.getGroupByTag(ygTag);
    // Tag search has no inherent order — surface the viewed product's color first so it
    // is the default-selected swatch.
    var seedId = String(product.id);
    if (siblingIds.indexOf(seedId) !== -1) {
      siblingIds = [seedId].concat(
        siblingIds.filter(function (id) {
          return id !== seedId;
        }),
      );
    }
  } else {
    siblingIds = await ShopifyStorefrontClient.getSiblingGroup(product.id);
  }
  if (!siblingIds || siblingIds.length === 0) return;

  // Fetch full product data (variants, images, metafields incl. swatch_override) for
  // every sibling from the Rebuy API — not the Storefront API, which is channel-limited
  // and doesn't expose cutline/swatch_override.
  var rebuyProducts = await RebuyClient.getProductsByIDs(siblingIds);
  if (!rebuyProducts || rebuyProducts.length === 0) return;

  var rebuyByProductId = {};
  rebuyProducts.forEach(function (rp) {
    rebuyByProductId[String(rp.id)] = rp;
  });

  // swatch_override stores a gid://shopify/MediaImage/… — collect each sibling's GID
  // from its Rebuy metafields and batch-resolve to image URLs in one Storefront call.
  var gidByProductId = {};
  rebuyProducts.forEach(function (rp) {
    var mf = metafieldsToMap(rp.metafields);
    var gid = mf.custom && mf.custom.swatch_override;
    if (gid && String(gid).indexOf("gid://shopify/MediaImage/") === 0) {
      gidByProductId[String(rp.id)] = gid;
    }
  });
  var swatchGids = Object.keys(gidByProductId).map(function (pid) {
    return gidByProductId[pid];
  });
  var mediaMap = await ShopifyStorefrontClient.resolveMediaImages(swatchGids);

  // Build the grouping descriptors in metaobject order, flattening variants/images onto
  // the widget product as we go. Siblings the Rebuy API didn't return are skipped.
  var grouping = [];
  var allVariants = [];
  var allImages = [];

  siblingIds.forEach(function (pid) {
    var rp = rebuyByProductId[String(pid)];
    if (!rp) return;

    var colorName = colorNameForRebuy(rp);
    var gid = gidByProductId[String(pid)];
    var swatchUrl =
      gid && mediaMap[gid]
        ? mediaMap[gid]
        : SWATCH_CDN_BASE + slugify(colorName) + ".png";

    var featured =
      (rp.image && rp.image.src) ||
      (rp.images && rp.images[0] && (rp.images[0].src || rp.images[0])) ||
      null;
    var hover =
      (rp.images && rp.images[1] && (rp.images[1].src || rp.images[1])) || null;
    var available = Array.isArray(rp.variants)
      ? rp.variants.some(function (v) {
          return v.available !== false;
        })
      : true;

    grouping.push({
      id: "gid://shopify/Product/" + rp.id,
      productId: String(rp.id),
      handle: rp.handle,
      url: "/products/" + rp.handle,
      colorName: colorName,
      swatchUrl: swatchUrl,
      available: available,
      image: featured,
      media: {
        nodes: (rp.images || []).map(function (im) {
          return { image: { url: im.src || im } };
        }),
      },
      hoverImage: hover,
    });

    // Flatten images for the card gallery
    if (featured) allImages.push({ src: featured, alt: colorName });
    if (hover) allImages.push({ src: hover, alt: colorName + " hover" });

    // Clone and annotate each variant with sibling metadata
    (rp.variants || []).forEach(function (v) {
      allVariants.push(
        Object.assign({}, v, {
          product_id: String(rp.id),
          product_title: rp.title || product.title,
          image_url: featured,
        }),
      );
    });
  });

  product.productGrouping = grouping;
  if (allVariants.length > 0) product.variants = allVariants;
  if (allImages.length > 0) product.images = allImages;
}

var GROUPING_METHODS = {
  // Returns the productGrouping array for a product, or [] if not stitched.
  siblingGroup: function (product) {
    return (product && product.productGrouping) || [];
  },

  // CSS style object for a swatch element; CSS :after renders --option-color-image.
  siblingSwatchStyle: function (sib) {
    var url = sib && sib.swatchUrl;
    return url
      ? {
          "--option-color-image": "url(" + url + ")",
          "--option-color": slugify(sib.colorName || ""),
        }
      : {};
  },

  // True if the given sibling is the currently selected color (falls back to first).
  siblingIsSelected: function (product, sib) {
    if (!product || !sib) return false;
    var sv = product.selected_variant;
    if (sv && sv.product_id) {
      return String(sv.product_id) === String(sib.productId);
    }
    var group = product.productGrouping;
    if (group && group.length > 0) {
      return String(group[0].productId) === String(sib.productId);
    }
    return false;
  },

  // Selects a sibling color via Rebuy's native selectVariantByOption so price/ATC
  // update natively; falls back to setting selected_variant directly.
  selectSibling: function (product, sib) {
    if (!product || !sib) return;
    var self = this;

    var variants = product.variants || [];
    var targetVariant = variants.find(function (v) {
      return (
        String(v.product_id) === String(sib.productId) &&
        (typeof self.variantAvailable === "function"
          ? self.variantAvailable(v)
          : true)
      );
    });
    if (!targetVariant) {
      targetVariant = variants.find(function (v) {
        return String(v.product_id) === String(sib.productId);
      });
    }
    if (!targetVariant) return;

    var options = product.options || [];
    var colorIdx = options.findIndex(function (o) {
      return (
        optName(o).toLowerCase().indexOf("color") !== -1 ||
        optName(o).toLowerCase().indexOf("colour") !== -1
      );
    });

    if (colorIdx !== -1 && typeof this.selectVariantByOption === "function") {
      var optionName = optName(options[colorIdx]);
      var colorValue = targetVariant["option" + (colorIdx + 1)];
      if (colorValue) {
        this.selectVariantByOption(product, optionName, colorValue);
        return;
      }
    }

    product.selected_variant = targetVariant;
    product.selected_variant_id = targetVariant.id;
    if (typeof this.$forceUpdate === "function") this.$forceUpdate();
  },

  // Raw image URLs for the currently selected sibling (its full media list, featured
  // first). Falls back to product.images for non-grouped products. Drives the hover-zone
  // gallery so the zones show the SELECTED color's images, not the union of all siblings.
  groupImageList: function (product) {
    var imgs = [];
    var group = product && product.productGrouping;
    if (group && group.length) {
      var sv = product.selected_variant;
      var selected = null;
      if (sv && sv.product_id) {
        selected = group.find(function (g) {
          return String(g.productId) === String(sv.product_id);
        });
      }
      if (!selected) selected = group[0];
      if (selected && selected.media && selected.media.nodes) {
        imgs = selected.media.nodes
          .map(function (n) {
            return n && n.image && n.image.url;
          })
          .filter(Boolean);
      }
    }
    if (!imgs.length && product && product.images) {
      imgs = product.images
        .map(function (im) {
          return im && (im.src || im);
        })
        .filter(Boolean);
    }
    return imgs;
  },

  // Sized src for the selected sibling's image at `index` (or '' if none).
  groupImageSrc: function (product, index, size) {
    var list =
      typeof this.groupImageList === "function"
        ? this.groupImageList(product)
        : [];
    var url = list[index];
    if (!url) return "";
    return typeof this.sizeImage === "function"
      ? this.sizeImage(url, size || "600x800")
      : url;
  },

  // Number of hover zones / pagination dots to render — selected sibling's image count
  // capped at 3.
  groupImageCount: function (product) {
    var list =
      typeof this.groupImageList === "function"
        ? this.groupImageList(product)
        : [];
    return Math.min(list.length, 3);
  },

  // Title for the currently selected sibling. Each color is its own product, so the
  // selected variant carries its product_title (set during stitch); falls back to the
  // seed product.title for non-grouped products.
  groupTitle: function (product) {
    var sv = product && product.selected_variant;
    if (sv && sv.product_title) return sv.product_title;
    return (product && product.title) || "";
  },

  // Primary card image src. Uses the selected sibling's featured image when the product
  // is grouped; otherwise falls back to Rebuy's native itemImage (selected variant) so
  // non-grouped products show the correct variant image, not a grouping image.
  cardPrimarySrc: function (product, size) {
    var group = product && product.productGrouping;
    if (group && group.length && typeof this.groupImageSrc === "function") {
      var src = this.groupImageSrc(product, 0, size);
      if (src) return src;
    }
    return typeof this.itemImage === "function"
      ? this.itemImage(product, product && product.selected_variant, size)
      : "";
  },

  // Hover image URL for the currently selected sibling (its 2nd media image).
  groupHoverImage: function (product) {
    if (!product || !product.productGrouping || !product.productGrouping.length)
      return null;
    var sv = product.selected_variant;
    var selected = null;
    if (sv && sv.product_id) {
      selected = product.productGrouping.find(function (g) {
        return String(g.productId) === String(sv.product_id);
      });
    }
    if (!selected) selected = product.productGrouping[0];
    if (!selected || !selected.hoverImage) return null;
    return typeof this.sizeImage === "function"
      ? this.sizeImage(selected.hoverImage, "600x800")
      : selected.hoverImage;
  },

  // Note: quick-view methods (openQuickView, closeQuickView, quickViewActiveProducts,
  // qvImageIndex, qvColors, qvSizeVariants, etc.) have been REMOVED from GROUPING_METHODS.
  // The quick view is now a SEPARATE Rebuy.libraries.Vue instance mounted in
  // #rebuy-quick-view-container (window.showQuickView in rebuy-extensions.liquid).
  // ATC delegates back to the widget via this.functions.addToCart(this.product).
};

// Install grouping methods on a widget's Vue constructor prototype (so the widget AND
// its nested child components inherit them) plus the instance. Patches EVERY widget's
// constructor — different Rebuy products (e.g. Smart Cart vs recommendation widgets) can
// use separate Vue builds, so a single global patch would miss some. Idempotent: the
// typeof guard skips already-installed methods.
function installGroupingMethods(vue) {
  if (!vue) return;

  var proto = vue.constructor && vue.constructor.prototype;
  if (proto) {
    Object.keys(GROUPING_METHODS).forEach(function (name) {
      if (typeof proto[name] !== "function")
        proto[name] = GROUPING_METHODS[name];
    });
  }

  Object.keys(GROUPING_METHODS).forEach(function (name) {
    if (typeof vue[name] !== "function") vue[name] = GROUPING_METHODS[name];
  });
}

// Patch Rebuy's shared Vue constructor prototype (Rebuy.libraries.Vue) the MOMENT it
// exists — before any widget renders. This is the only fully reliable way to avoid a
// first-paint "<method> is not defined" render error: Rebuy renders some widgets (e.g.
// the Smart Cart cross-sell) before dispatching rebuy.init, so waiting for init is too
// late. We load before the SDK (see rebuy-scripts.liquid), then poll for the Vue lib.
function patchRebuyVuePrototype() {
  try {
    var V = window.Rebuy && Rebuy.libraries && Rebuy.libraries.Vue;
    if (V && V.prototype) {
      Object.keys(GROUPING_METHODS).forEach(function (name) {
        if (typeof V.prototype[name] !== "function")
          V.prototype[name] = GROUPING_METHODS[name];
      });
      return true;
    }
  } catch (e) {
    /* noop */
  }
  return false;
}
if (!patchRebuyVuePrototype()) {
  var _patchPoll = setInterval(function () {
    if (patchRebuyVuePrototype()) clearInterval(_patchPoll);
  }, 20);
  setTimeout(function () {
    clearInterval(_patchPoll);
  }, 8000);
}
document.addEventListener("rebuy.loaded", patchRebuyVuePrototype);

// ESC key closes the quick-view panel via the standalone Vue instance's mounted handler
// (see window.showQuickView in rebuy-extensions.liquid — no widget-level handler needed).

document.addEventListener("rebuy.init", function (event) {
  var widget = event && event.detail && event.detail.widget;
  installGroupingMethods(widget && widget.View && widget.View.vue);
});

// Belt-and-suspenders: install from any already-initialized widget once Rebuy is
// ready, in case a child renders before its parent widget's rebuy.init fires.
document.addEventListener("rebuy.ready", function () {
  if (!window.Rebuy || !Array.isArray(Rebuy.widgets)) return;
  for (var i = 0; i < Rebuy.widgets.length; i++) {
    var w = Rebuy.widgets[i];
    if (w && w.View && w.View.vue) {
      installGroupingMethods(w.View.vue);
    }
  }
});

// ─── PRODUCTS CHANGE LISTENER ──────────────────────────────────────────────

document.addEventListener("rebuy.productsChange", async function (event) {
  var widget = event && event.detail && event.detail.widget;
  if (!widget || !widget.data || !Array.isArray(widget.data.products)) return;

  var vue = widget.View && widget.View.vue;

  // Stitch every product in parallel (Oofos pattern) so the slowest sibling fetch
  // gates the batch, not the sum of them, then force one re-render.
  await Promise.all(
    widget.data.products.map(function (product) {
      return stitchGroupToProduct(product);
    }),
  );

  if (vue) vue.$forceUpdate();
});

// Immediate install sweep: the Rebuy SDK loads in <head> while this asset is a
// body-end deferred script, so a widget can initialize (and render the template,
// which calls groupHoverImage/siblingGroup) before our rebuy.init listener attaches.
// If Rebuy already has live widgets when this runs, install the methods now and force
// a re-render to clear any failed first paint. Harmless if Rebuy isn't ready yet.
(function installOnExistingWidgets() {
  if (!window.Rebuy || !Array.isArray(Rebuy.widgets)) return;
  Rebuy.widgets.forEach(function (w) {
    var vue = w && w.View && w.View.vue;
    if (vue) {
      installGroupingMethods(vue);
      if (typeof vue.$forceUpdate === "function") vue.$forceUpdate();
    }
  });
})();
